import React, { useState } from 'react';
import { IconX, IconUpload, IconUser } from '@tabler/icons-react';
interface AddChildModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (child: {
    name: string;
    birthDate: string;
    avatar?: string;
  }) => void;
}
export function AddChildModal({
  isOpen,
  onClose,
  onSubmit
}: AddChildModalProps) {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [avatar, setAvatar] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && birthDate) {
      onSubmit({
        name: name.trim(),
        birthDate,
        avatar: previewUrl || undefined
      });
      // Reset form
      setName('');
      setBirthDate('');
      setAvatar('');
      setPreviewUrl('');
      onClose();
    }
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const calculateAge = (birthDateString: string) => {
    if (!birthDateString) return '';
    const today = new Date();
    const birth = new Date(birthDateString);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birth.getDate()) {
      age--;
    }
    return age > 0 ? `${age} ${age === 1 ? 'anno' : 'anni'}` : 'Meno di 1 anno';
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-yeppy-text bg-opacity-30 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-37">
        {/* Modal */}
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-38">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-yeppy-text border-opacity-10 px-6 py-4 rounded-t-3xl" data-id="element-39">
            <div className="flex items-center justify-between" data-id="element-40">
              <h2 className="font-rubik font-bold text-xl text-yeppy-text" data-id="element-41">
                Aggiungi bambino
              </h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-yeppy-bg text-yeppy-text transition-colors" data-id="element-42">
                <IconX className="w-5 h-5" stroke={1.5} data-id="element-43" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5" data-id="element-44">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center gap-3" data-id="element-45">
              <div className="relative" data-id="element-46">
                {previewUrl ? <img src={previewUrl} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2 border-yeppy-text border-opacity-10" data-id="element-47" /> : <div className="w-24 h-24 rounded-full bg-yeppy-bg border-2 border-dashed border-yeppy-text border-opacity-20 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-opacity-40 transition-colors" data-id="element-48">
                    <IconUpload className="w-6 h-6 text-yeppy-text text-opacity-40" stroke={1.5} data-id="element-49" />
                    <span className="font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-50">
                      Carica foto
                    </span>
                  </div>}
                <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-colors" style={{
                backgroundColor: '#1D2A36'
              }} data-id="element-51">
                  <IconUpload className="w-4 h-4" stroke={1.5} style={{
                  color: '#FFFFFB'
                }} data-id="element-52" />
                  <input id="avatar-upload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-53" />
                </label>
              </div>
              <p className="font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-54">
                Foto profilo (opzionale)
              </p>
            </div>

            {/* Name Input */}
            <div data-id="element-55">
              <label htmlFor="child-name" className="block font-poppins text-sm font-medium text-yeppy-text mb-2" data-id="element-56">
                Nome *
              </label>
              <div className="relative" data-id="element-57">
                <IconUser className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yeppy-text text-opacity-40" stroke={1.5} data-id="element-58" />
                <input id="child-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Es. Sofia" required className="w-full px-4 py-3 rounded-xl font-poppins text-sm text-yeppy-text bg-white border border-yeppy-text border-opacity-15 focus:outline-none focus:border-yeppy-blue focus:border-opacity-50 transition-colors placeholder-yeppy-text placeholder-opacity-40" data-id="element-59" />
              </div>
            </div>

            {/* Birth Date Input */}
            <div data-id="element-60">
              <label htmlFor="birth-date" className="block font-poppins text-sm font-medium text-yeppy-text mb-2" data-id="element-61">
                Data di nascita *
              </label>
              <input id="birth-date" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} required max={new Date().toISOString().split('T')[0]} className="w-full px-4 py-3 rounded-xl font-poppins text-sm text-yeppy-text bg-white border border-yeppy-text border-opacity-15 focus:outline-none focus:border-yeppy-blue focus:border-opacity-50 transition-colors" data-id="element-62" />
              {birthDate && <p className="mt-2 font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-63">
                  Età: {calculateAge(birthDate)}
                </p>}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2" data-id="element-64">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-xl font-poppins text-sm font-semibold text-yeppy-text bg-white border border-yeppy-text border-opacity-15 transition-colors" data-id="element-65">
                Annulla
              </button>
              <button type="submit" disabled={!name.trim() || !birthDate} className="flex-1 px-4 py-3 rounded-xl font-poppins text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{
              backgroundColor: name.trim() && birthDate ? '#1D2A36' : '#1D2A36',
              color: '#FFFFFB'
            }} data-id="element-66">
                Aggiungi
              </button>
            </div>
          </form>
        </div>
      </div>
    </>;
}